/**
 * The Gini coefficient and the Lorenz curve it comes from.
 *
 * For n incomes sorted from smallest to largest, x₍₁₎ ≤ … ≤ x₍ₙ₎, with mean μ:
 *
 *   G = Σᵢ Σⱼ |xᵢ − xⱼ| ÷ (2 n² μ)  =  2 Σ i·x₍ᵢ₎ ÷ (n Σ x) − (n + 1) ÷ n
 *
 * The two forms are the same number. G is the area between the line of equality and the
 * Lorenz curve divided by the area under the line of equality, so 0 is perfect equality
 * and the largest possible value for n people is (n − 1) ÷ n, reached when one person has
 * everything. The small-sample variant G × n ÷ (n − 1) rescales that maximum to 1 (it is
 * the form that treats the n values as a sample from a larger population).
 */

export interface Gini {
  n: number;
  mean: number;
  total: number;
  gini: number;
  /** G × n ÷ (n − 1), or null for a single value. */
  corrected: number | null;
  /** The largest G possible for n values, (n − 1) ÷ n. */
  maximum: number;
  /** Σ i·x₍ᵢ₎ over the sorted values, the sum in the second form of the formula. */
  weightedSum: number;
  /** The incomes from smallest to largest. */
  sorted: number[];
}

export type GiniError = "too-few" | "negative" | "zero-total";

/** The Gini coefficient of individual incomes, or why there is none. */
export function giniOf(values: readonly number[]): Gini | GiniError {
  if (values.length < 2) {
    return "too-few";
  }
  if (values.some((v) => v < 0)) {
    return "negative";
  }
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const total = sorted.reduce((sum, v) => sum + v, 0);
  if (total === 0) {
    return "zero-total";
  }
  let weighted = 0;
  for (const [index, value] of sorted.entries()) {
    weighted += (index + 1) * value;
  }
  const gini = (2 * weighted) / (n * total) - (n + 1) / n;
  return {
    n,
    mean: total / n,
    total,
    gini: Math.max(0, gini),
    corrected: (Math.max(0, gini) * n) / (n - 1),
    maximum: (n - 1) / n,
    weightedSum: weighted,
    sorted,
  };
}

/** The same Gini by averaging every pairwise difference — the definition, used to check. */
export function giniByPairs(values: readonly number[]): number {
  const n = values.length;
  const total = values.reduce((sum, v) => sum + v, 0);
  let sum = 0;
  for (const a of values) {
    for (const b of values) {
      sum += Math.abs(a - b);
    }
  }
  return sum / (2 * n * total);
}

export type LorenzPoint = readonly [number, number];

/** Lorenz points in percent: (0, 0), then the cumulative population and income shares. */
export function lorenzOf(values: readonly number[]): LorenzPoint[] {
  const sorted = [...values].sort((a, b) => a - b);
  const total = sorted.reduce((sum, v) => sum + v, 0);
  const points: LorenzPoint[] = [[0, 0]];
  let running = 0;
  for (const [index, value] of sorted.entries()) {
    running += value;
    points.push([((index + 1) / sorted.length) * 100, (running / total) * 100]);
  }
  return points;
}

export interface Group {
  /** Share of the population, in percent. */
  population: number;
  /** Share of total income, in percent. */
  income: number;
}

export type GroupedError = "too-few" | "negative" | "zero-total";

export interface GroupedGini {
  gini: number;
  /** Lorenz points in percent, groups ordered from poorest to richest. */
  lorenz: LorenzPoint[];
  /** Whether the groups were already in poorest-first order. */
  reordered: boolean;
}

/**
 * The Gini of grouped data by the trapezoid rule on the Lorenz curve:
 * G = 1 − Σ (Xₖ − Xₖ₋₁)(Yₖ + Yₖ₋₁), with cumulative population shares X and income
 * shares Y as fractions. It joins the group points with straight lines, so it ignores
 * inequality inside each group and is a lower bound for the true Gini.
 */
export function giniOfGroups(
  groups: readonly Group[]
): GroupedGini | GroupedError {
  if (groups.length < 2) {
    return "too-few";
  }
  if (groups.some((g) => g.population < 0 || g.income < 0)) {
    return "negative";
  }
  const people = groups.reduce((sum, g) => sum + g.population, 0);
  const income = groups.reduce((sum, g) => sum + g.income, 0);
  if (!(people > 0 && income > 0)) {
    return "zero-total";
  }
  // Poorest first: ascending income per person.
  const ordered = [...groups].sort(
    (a, b) => a.income * b.population - b.income * a.population
  );
  const reordered = ordered.some((g, i) => g !== groups[i]);
  const lorenz: LorenzPoint[] = [[0, 0]];
  let x = 0;
  let y = 0;
  let area = 0;
  for (const g of ordered) {
    const nextX = x + g.population / people;
    const nextY = y + g.income / income;
    area += (nextX - x) * (nextY + y);
    x = nextX;
    y = nextY;
    lorenz.push([x * 100, y * 100]);
  }
  return { gini: Math.max(0, 1 - area), lorenz, reordered };
}

/** At most `limit` points of a long Lorenz curve, always keeping both ends. */
export function thin(
  points: readonly LorenzPoint[],
  limit: number
): LorenzPoint[] {
  if (points.length <= limit) {
    return [...points];
  }
  const step = (points.length - 1) / (limit - 1);
  return Array.from({ length: limit }, (_, i) => points[Math.round(i * step)]);
}
